import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  MultiSelect,
  Paper,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import type {
  ApplicationAnswers,
  ApplicationField,
  ApplicationTemplate,
  ApplicationTemplateType,
  ApplicationType,
} from "@wwrp/shared";
import { applicationsApi } from "../api/applications.js";
import { useAuth } from "../contexts/AuthContext.js";
import { hasAtLeast } from "@wwrp/shared";
import { departmentsApi } from "../api/department-options.js";
import { getAccessRole } from "../api/auth.js";
import {
  APPLICATION_TYPE_LABELS,
  APPLICATION_STATUS_LABELS,
  applicationPlacementLabel,
  CreateApplicationTemplateSchema,
  ApplicationTemplateTypeSchema,
  APPLICATION_QUESTION_MAX_LENGTH,
} from "@wwrp/shared";
import { changeApplicationFieldType } from "../application-fields.js";
import {
  ApplicationAnswersView,
  ApplicationApplicantSummary,
  ApplicationDiscussion,
  ApplicationReviewSection,
  ApplicationActivityLog,
} from "../components/ApplicationReviewContent.js";

function Fields({
  fields,
  answers,
  setAnswers,
}: {
  fields: ApplicationField[];
  answers: ApplicationAnswers;
  setAnswers: (v: ApplicationAnswers) => void;
}) {
  return (
    <Stack>
      {fields.map((field) => {
        if (field.type === "section_header")
          return (
            <Title key={field.id} order={3}>
              {field.label}
            </Title>
          );
        const answer = answers[field.id];
        const set = (value: string) =>
          setAnswers({ ...answers, [field.id]: { type: "text", value } });
        if (field.type === "single_choice")
          return (
            <Select
              key={field.id}
              label={field.label}
              description={field.description}
              required={field.required}
              data={field.options}
              value={answer?.type === "choice" ? answer.value : null}
              onChange={(v) =>
                setAnswers({ ...answers, [field.id]: { type: "choice", value: v ?? "" } })
              }
            />
          );
        if (field.type === "multi_choice")
          return (
            <MultiSelect
              key={field.id}
              label={field.label}
              description={field.description}
              required={field.required}
              data={field.options}
              value={answer?.type === "choices" ? answer.value : []}
              onChange={(v) =>
                setAnswers({ ...answers, [field.id]: { type: "choices", value: v } })
              }
            />
          );
        if (field.type === "long_text")
          return (
            <Textarea
              key={field.id}
              label={field.label}
              description={field.description}
              required={field.required}
              minRows={4}
              autosize
              value={answer?.type === "text" ? answer.value : ""}
              onChange={(e) => set(e.currentTarget.value)}
            />
          );
        if (field.type === "number")
          return (
            <TextInput
              key={field.id}
              type="number"
              label={field.label}
              required={field.required}
              value={answer?.type === "number" ? String(answer.value) : ""}
              onChange={(e) =>
                setAnswers({
                  ...answers,
                  [field.id]: { type: "number", value: Number(e.currentTarget.value) },
                })
              }
            />
          );
        if (field.type === "date")
          return (
            <TextInput
              key={field.id}
              type="date"
              label={field.label}
              required={field.required}
              value={answer?.type === "date" ? answer.value : ""}
              onChange={(e) =>
                setAnswers({
                  ...answers,
                  [field.id]: { type: "date", value: e.currentTarget.value },
                })
              }
            />
          );
        return (
          <TextInput
            key={field.id}
            type={field.type === "email" ? "email" : "text"}
            label={field.label}
            description={field.description}
            required={field.required}
            value={answer?.type === "text" ? answer.value : ""}
            onChange={(e) => set(e.currentTarget.value)}
          />
        );
      })}
    </Stack>
  );
}

export function ApplicationsPage({ view = "apply" }: { view?: "apply" | "review" | "manage" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = Boolean(user && hasAtLeast(getAccessRole(user)!, "senior_administration"));
  const templates = useQuery({
    queryKey: ["application-templates"],
    queryFn: applicationsApi.templates,
  });
  const departments = useQuery({
    queryKey: ["application-departments"],
    queryFn: departmentsApi.list,
  });
  const list = useQuery({
    queryKey: ["applications"],
    queryFn: applicationsApi.list,
    enabled: view === "review",
  });
  const [selected, setSelected] = useState<string | null>(null);
  const [reviewingApplication, setReviewingApplication] = useState(false);
  const template = templates.data?.find((t) => String(t.id) === selected);
  const departmentName = (id: number | null) =>
    departments.data?.find((d) => d.id === id)?.name ?? "Department";
  if (view === "manage")
    return (
      <Stack className="page" gap="xl">
        <Title>Manage Applications</Title>
        <Text c="dimmed">
          Create applications, edit their questions, and control when people can apply.
        </Text>
        {templates.isLoading && <Text c="dimmed">Loading applications…</Text>}
        {canManage && (
          <ApplicationManagement
            applications={templates.data ?? []}
            departments={departments.data ?? []}
            loading={templates.isLoading}
          />
        )}
        {templates.error && <Alert color="red">{templates.error.message}</Alert>}
      </Stack>
    );
  if (view === "review")
    return (
      <Stack className="page" gap="xl">
        <Title>Application Review</Title>
        <Text c="dimmed">Review reserve and transfer applications.</Text>
        {list.isLoading && <Text c="dimmed">Loading applications…</Text>}
        {list.error && <Alert color="red">{list.error.message}</Alert>}
        {list.data?.length === 0 && <Text c="dimmed">No applications to review.</Text>}
        {(list.data ?? []).map((a) => (
          <Card key={a.id} component={Link} to={`/staff/applications/${a.id}`}>
            <Group justify="space-between">
              <Text>{a.templateTitle}</Text>
              <Badge>{APPLICATION_STATUS_LABELS[a.status]}</Badge>
            </Group>
            <Text size="sm" fw={600}>
              {a.applicantName ?? `Member #${a.applicantId}`}
            </Text>
            <Text size="sm" c="dimmed">
              {applicationPlacementLabel(a)}
            </Text>
            <Text size="xs" c="dimmed">
              {a.applicantWebsiteId ? `Website ID ${a.applicantWebsiteId} · ` : ""}Submitted{" "}
              {new Date(a.createdAt).toLocaleString()}
            </Text>
          </Card>
        ))}
      </Stack>
    );
  return (
    <Stack className="page" gap="xl">
      <Title>Applications</Title>
      <Text c="dimmed">Request a transfer or reserve placement and follow its review.</Text>
      <Tabs defaultValue="new">
        <Tabs.List>
          <Tabs.Tab value="new">New application</Tabs.Tab>
          <Tabs.Tab value="mine">My applications</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="new" pt="lg">
          <Stack maw={760}>
            <Select
              label="Department and application type"
              placeholder="Choose an open application"
              data={(templates.data ?? [])
                .filter((t) => t.status === "open")
                .map((t) => ({
                  value: String(t.id),
                  label: `${departmentName(t.targetDepartmentId)} · ${APPLICATION_TYPE_LABELS[t.type]} · ${t.title}`,
                }))}
              value={selected}
              disabled={reviewingApplication}
              onChange={(value) => {
                if (!reviewingApplication) setSelected(value);
              }}
            />
            {template && (
              <ApplicationForm
                key={template.id}
                template={template}
                onReviewChange={setReviewingApplication}
                onSubmitted={(id) => navigate(`/applications/${id}`)}
              />
            )}
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="mine" pt="lg">
          <MyApplications />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

export function MyApplications() {
  const { user } = useAuth();
  const list = useQuery({ queryKey: ["applications"], queryFn: applicationsApi.list });
  const mine = (list.data ?? []).filter((a) => a.applicantId === user?.id);
  return (
    <Stack mb="lg">
      <Group justify="space-between">
        <Title order={3}>My applications</Title>
      </Group>
      {list.isLoading && <Text c="dimmed">Loading applications…</Text>}
      {list.error && <Alert color="red">{list.error.message}</Alert>}
      {list.isSuccess && mine.length === 0 && (
        <Text c="dimmed">You have no reserve or transfer applications yet.</Text>
      )}
      {mine.map((a) => (
        <Card key={a.id} component={Link} to={`/applications/${a.id}`}>
          <Group justify="space-between">
            <Text>{a.templateTitle}</Text>
            <Badge>{APPLICATION_STATUS_LABELS[a.status]}</Badge>
          </Group>
          <Text size="sm" c="dimmed">
            {applicationPlacementLabel(a)}
          </Text>
          <Text size="xs" c="dimmed">
            Submitted {new Date(a.createdAt).toLocaleString()}
          </Text>
        </Card>
      ))}
    </Stack>
  );
}

export function ApplicationForm({
  template,
  onSubmitted,
  onReviewChange,
}: {
  template: ApplicationTemplate;
  onSubmitted: (id: number) => void;
  onReviewChange?: (reviewing: boolean) => void;
}) {
  const [answers, setAnswers] = useState<ApplicationAnswers>({});
  const [review, setReview] = useState(false);
  const [selectedType, setSelectedType] = useState<ApplicationType | null>(null);
  const [typeError, setTypeError] = useState("");
  const type = template.type === "transfer_or_reserve" ? selectedType : template.type;
  const client = useQueryClient();
  const submit = useMutation({
    mutationFn: () => applicationsApi.submit(template.id, answers, type ?? undefined),
    onSuccess: ({ id }) => {
      void client.invalidateQueries({ queryKey: ["applications"] });
      onSubmitted(id);
    },
  });
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!type) {
          setTypeError("Choose Transfer or Reserve before continuing.");
          setReview(false);
          onReviewChange?.(false);
          return;
        }
        if (review) submit.mutate();
        else {
          setReview(true);
          onReviewChange?.(true);
        }
      }}
    >
      <Stack>
        <Title order={3}>{template.title}</Title>
        {template.description && <Text c="dimmed">{template.description}</Text>}
        {review ? (
          <Stack>
            <Title order={4}>Review your answers</Title>
            <Text>
              <b>Application type:</b> {type && APPLICATION_TYPE_LABELS[type]}
            </Text>
            <ApplicationAnswersView fields={template.fields} answers={answers} />
          </Stack>
        ) : (
          <>
            {template.type === "transfer_or_reserve" ? (
              <Select
                label="Application type"
                placeholder="Choose Transfer or Reserve"
                description="Transfer requires both departments’ approval. Reserve requires only the receiving department’s approval."
                required
                data={[
                  { value: "transfer", label: "Transfer" },
                  { value: "reserve", label: "Reserve" },
                ]}
                value={selectedType}
                error={typeError}
                onChange={(value) => {
                  setSelectedType(value as ApplicationType | null);
                  setTypeError("");
                }}
              />
            ) : (
              <Text>
                <b>Application type:</b> {APPLICATION_TYPE_LABELS[template.type]}
              </Text>
            )}
            <Fields fields={template.fields} answers={answers} setAnswers={setAnswers} />
          </>
        )}
        {submit.error && <Alert color="red">{submit.error.message}</Alert>}
        <Group justify="flex-end">
          {review && (
            <Button
              variant="light"
              disabled={submit.isPending}
              onClick={() => {
                setReview(false);
                onReviewChange?.(false);
              }}
            >
              Edit answers
            </Button>
          )}
          <Button type="submit" loading={submit.isPending}>
            {review ? "Submit application" : "Review answers"}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

export function ApplicationManagement({
  applications,
  departments,
  loading = false,
}: {
  applications: ApplicationTemplate[];
  departments: Awaited<ReturnType<typeof departmentsApi.list>>;
  loading?: boolean;
}) {
  const client = useQueryClient();
  const [tab, setTab] = useState<string | null>("applications");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [notice, setNotice] = useState("");
  const editing = applications.find((application) => application.id === editingId);
  const availability = useMutation({
    mutationFn: ({ id, status }: { id: number; status: "open" | "closed" }) =>
      applicationsApi.updateTemplate(id, { status }),
    onSuccess: async (_data, { status }) => {
      await client.invalidateQueries({ queryKey: ["application-templates"] });
      setNotice(
        status === "open"
          ? "Application opened. People can now apply."
          : "Application turned off. New submissions are closed.",
      );
    },
  });
  const saved = async () => {
    await client.invalidateQueries({ queryKey: ["application-templates"] });
    setNotice(tab === "new" ? "Application created." : "Application saved.");
    setTab("applications");
    setEditingId(null);
  };
  return (
    <Stack>
      {notice && (
        <Alert color="teal" role="status" onClose={() => setNotice("")} withCloseButton>
          {notice}
        </Alert>
      )}
      {availability.error && <Alert color="red">{availability.error.message}</Alert>}
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="applications">Applications ({applications.length})</Tabs.Tab>
          <Tabs.Tab value="new">Create application</Tabs.Tab>
          {editing && <Tabs.Tab value="edit">Edit: {editing.title}</Tabs.Tab>}
        </Tabs.List>
        <Tabs.Panel value="applications" pt="lg">
          {!loading && applications.length === 0 ? (
            <Stack align="flex-start">
              <Text c="dimmed">
                No applications yet. Create one to set up its questions and availability.
              </Text>
              <Button onClick={() => setTab("new")}>Create application</Button>
            </Stack>
          ) : (
            applications.length > 0 && (
              <Table.ScrollContainer minWidth={700}>
                <Table verticalSpacing="md" highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Application</Table.Th>
                      <Table.Th>Department</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Availability</Table.Th>
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {applications.map((application) => (
                      <Table.Tr key={application.id}>
                        <Table.Td>
                          <Text fw={600}>{application.title}</Text>
                        </Table.Td>
                        <Table.Td>
                          {departments.find(
                            (department) => department.id === application.targetDepartmentId,
                          )?.name ?? "No department selected"}
                        </Table.Td>
                        <Table.Td>{APPLICATION_TYPE_LABELS[application.type]}</Table.Td>
                        <Table.Td>
                          <Badge color={application.status === "open" ? "teal" : "gray"}>
                            {application.status === "open"
                              ? "Open"
                              : application.status === "draft"
                                ? "Draft"
                                : "Off"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs" wrap="nowrap">
                            <Button
                              size="xs"
                              variant="light"
                              aria-label={`Edit ${application.title}`}
                              onClick={() => {
                                setEditingId(application.id);
                                setTab("edit");
                                setNotice("");
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="xs"
                              variant="subtle"
                              aria-label={`${application.status === "open" ? "Turn off" : "Open"} ${application.title}`}
                              loading={
                                availability.isPending &&
                                availability.variables?.id === application.id
                              }
                              disabled={availability.isPending}
                              onClick={() => {
                                setNotice("");
                                availability.mutate({
                                  id: application.id,
                                  status: application.status === "open" ? "closed" : "open",
                                });
                              }}
                            >
                              {application.status === "open" ? "Turn off" : "Open"}
                            </Button>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )
          )}
        </Tabs.Panel>
        <Tabs.Panel value="new" pt="lg">
          <ApplicationEditor departments={departments} onSaved={saved} />
        </Tabs.Panel>
        {editing && (
          <Tabs.Panel value="edit" pt="lg">
            <ApplicationEditor
              key={editing.id}
              application={editing}
              departments={departments}
              onSaved={saved}
            />
          </Tabs.Panel>
        )}
      </Tabs>
    </Stack>
  );
}

function ApplicationEditor({
  application,
  departments,
  onSaved,
}: {
  application?: ApplicationTemplate;
  departments: Awaited<ReturnType<typeof departmentsApi.list>>;
  onSaved: () => Promise<void>;
}) {
  const [title, setTitle] = useState(application?.title ?? "");
  const [description, setDescription] = useState(application?.description ?? "");
  const [kind, setKind] = useState<ApplicationTemplateType | null>(() => {
    const parsed = ApplicationTemplateTypeSchema.safeParse(application?.type ?? "transfer");
    return parsed.success ? parsed.data : null;
  });
  const [department, setDepartment] = useState(String(application?.targetDepartmentId ?? ""));
  const [fields, setFields] = useState<ApplicationField[]>(application?.fields ?? []);
  const [status, setStatus] = useState<"draft" | "open" | "closed">(application?.status ?? "draft");
  const save = useMutation({
    mutationFn: () => {
      const body = {
        title,
        description,
        type: kind,
        targetDepartmentId: Number(department),
        status,
        fields,
      };
      const parsed = CreateApplicationTemplateSchema.safeParse(body);
      if (!parsed.success) {
        throw new Error(
          parsed.error.issues
            .map((issue) => {
              const path = issue.path;
              if (path[0] === "fields" && typeof path[1] === "number") {
                const question = fields[path[1]];
                const label = `Question ${path[1] + 1}${question?.label ? ` (${question.label})` : ""}`;
                return `${label}: ${path[2] === "options" ? "add at least one non-empty answer option (maximum 50, 200 characters each)." : issue.message}`;
              }
              if (path[0] === "type")
                return "Choose an application type: Transfer only, Reserve only, or Member chooses Transfer or Reserve.";
              return `${path.join(".")}: ${issue.message}`;
            })
            .join(" "),
        );
      }
      return application
        ? applicationsApi.updateTemplate(application.id, parsed.data)
        : applicationsApi.createTemplate(parsed.data);
    },
    onSuccess: onSaved,
  });
  return (
    <Stack className="admin-request-form">
      <div className="admin-request-form__layout">
        <section className="admin-request-form__section admin-request-form__details">
          <div className="admin-request-form__section-heading">
            <div>
              <Title order={2}>Application details</Title>
              <Text size="sm" c="dimmed">
                The name and description members see when applying.
              </Text>
            </div>
          </div>
          <Stack>
            <TextInput
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
            />
            <Textarea
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              autosize
              minRows={3}
            />
          </Stack>
        </section>
        <aside className="admin-request-form__section admin-request-form__routing">
          <div className="admin-request-form__section-heading">
            <div>
              <Title order={2}>Submission settings</Title>
              <Text size="sm" c="dimmed">
                Choose the department, application types, and availability.
              </Text>
            </div>
          </div>
          <Stack>
            <Select
              label="Application type"
              data={[
                { value: "transfer", label: "Transfer only" },
                { value: "reserve", label: "Reserve only" },
                { value: "transfer_or_reserve", label: "Member chooses Transfer or Reserve" },
              ]}
              value={kind}
              error={kind === null ? "Choose a valid application type before saving." : undefined}
              allowDeselect={false}
              onChange={(v) => {
                const parsedType = ApplicationTemplateTypeSchema.safeParse(v);
                setKind(parsedType.success ? parsedType.data : null);
              }}
            />
            <Select
              label="Destination department"
              data={departments.map((d) => ({ value: String(d.id), label: d.name }))}
              value={department || null}
              onChange={(v) => setDepartment(v ?? "")}
            />
            <Select
              label="Availability"
              data={[
                { value: "draft", label: "Draft — not accepting submissions" },
                { value: "open", label: "Open — accepting submissions" },
                { value: "closed", label: "Off — submissions closed" },
              ]}
              value={status}
              allowDeselect={false}
              onChange={(v) => {
                if (v) setStatus(v as typeof status);
              }}
            />
            <Text size="sm" c="dimmed">
              Reserve requires the receiving department’s approval. Transfer requires both
              departments’ approval.
            </Text>
          </Stack>
        </aside>
        <section className="admin-request-form__section admin-request-form__materials">
          <div className="admin-request-form__section-heading">
            <div>
              <Title order={2}>Questions</Title>
              <Text size="sm" c="dimmed">
                Members answer these questions for every supported application type.
              </Text>
            </div>
            <Badge variant="light">
              {fields.length} {fields.length === 1 ? "question" : "questions"}
            </Badge>
          </div>
          <Stack>
            {fields.length === 0 && (
              <Text size="sm" c="dimmed">
                Add a question to get started.
              </Text>
            )}
            {fields.map((f, index) => (
              <Stack
                key={f.id}
                gap="sm"
                pb="lg"
                style={{ borderBottom: "1px solid var(--border)" }}
              >
                <Textarea
                  aria-label="Question"
                  label={`Question ${index + 1}`}
                  autosize
                  minRows={2}
                  maxRows={12}
                  description={`${f.label.length.toLocaleString()} / ${APPLICATION_QUESTION_MAX_LENGTH.toLocaleString()} characters`}
                  error={
                    f.label.length > APPLICATION_QUESTION_MAX_LENGTH
                      ? "Shorten this question before saving."
                      : undefined
                  }
                  value={f.label}
                  onChange={(e) =>
                    setFields(
                      fields.map((item, i) =>
                        i === index ? { ...item, label: e.currentTarget.value } : item,
                      ),
                    )
                  }
                />
                <Select
                  aria-label="Answer type"
                  label="Answer type"
                  data={[
                    { value: "short_text", label: "Short answer" },
                    { value: "long_text", label: "Long answer" },
                    { value: "email", label: "Email address" },
                    { value: "number", label: "Number" },
                    { value: "date", label: "Date" },
                    { value: "single_choice", label: "Choose one" },
                    { value: "multi_choice", label: "Choose multiple" },
                    { value: "section_header", label: "Section heading" },
                  ]}
                  value={f.type}
                  onChange={(v) =>
                    setFields(
                      fields.map((item, i) =>
                        i === index
                          ? changeApplicationFieldType(
                              item,
                              (v ?? item.type) as ApplicationField["type"],
                            )
                          : item,
                      ),
                    )
                  }
                />
                <TextInput
                  label="Help text"
                  value={f.description ?? ""}
                  onChange={(e) =>
                    setFields(
                      fields.map((item, i) =>
                        i === index ? { ...item, description: e.currentTarget.value } : item,
                      ),
                    )
                  }
                />
                {"options" in f && (
                  <TextInput
                    label="Options (comma separated)"
                    value={f.options.join(", ")}
                    onChange={(e) =>
                      setFields(
                        fields.map((item, i) =>
                          i === index
                            ? ({
                                ...item,
                                options: e.currentTarget.value
                                  .split(",")
                                  .map((v) => v.trim())
                                  .filter(Boolean),
                              } as ApplicationField)
                            : item,
                        ),
                      )
                    }
                  />
                )}
                <Group mt="sm">
                  <Button
                    variant={f.required ? "filled" : "light"}
                    onClick={() =>
                      setFields(
                        fields.map((item, i) =>
                          i === index ? { ...item, required: !item.required } : item,
                        ),
                      )
                    }
                  >
                    {f.required ? "Required" : "Optional"}
                  </Button>
                  <Button
                    variant="subtle"
                    disabled={index === 0}
                    onClick={() => {
                      const next = [...fields];
                      [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
                      setFields(next);
                    }}
                  >
                    Move up
                  </Button>
                  <Button
                    color="red"
                    variant="subtle"
                    onClick={() => setFields(fields.filter((item) => item.id !== f.id))}
                  >
                    Remove
                  </Button>
                </Group>
              </Stack>
            ))}
            <Button
              variant="light"
              onClick={() =>
                setFields([
                  ...fields,
                  {
                    id: crypto.randomUUID(),
                    label: "New question",
                    required: true,
                    type: "long_text",
                  },
                ])
              }
            >
              Add question
            </Button>
          </Stack>
        </section>
      </div>
      {save.error && <Alert color="red">{save.error.message}</Alert>}
      <Group className="admin-request-form__actions" justify="flex-end">
        <Button
          className="admin-request-form__submit"
          disabled={!title.trim() || !department || !fields.length}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          {application ? "Save application" : "Create application"}
        </Button>
      </Group>
    </Stack>
  );
}

export function ApplicationDetailPage() {
  const { user } = useAuth();
  const id = Number(useParams().id),
    client = useQueryClient();
  const query = useQuery({
    queryKey: ["application", id],
    queryFn: () => applicationsApi.detail(id),
  });
  const [message, setMessage] = useState(""),
    [privateNote, setPrivateNote] = useState("");
  const action = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["application", id] });
      client.invalidateQueries({ queryKey: ["applications"] });
    },
  });
  const a = query.data;
  if (!a)
    return (
      <Stack className="page">
        <Title>Application</Title>
        {query.error && <Alert color="red">{query.error.message}</Alert>}
      </Stack>
    );
  return (
    <Stack className="page" gap="lg" style={{ minWidth: 0 }}>
      <Group justify="space-between" align="flex-start">
        <Title style={{ overflowWrap: "anywhere", minWidth: 0 }}>{a.templateTitle}</Title>
        {a.actions.withdraw && (
          <Button
            color="red"
            size="sm"
            variant="subtle"
            disabled={action.isPending}
            onClick={() => action.mutate(() => applicationsApi.withdraw(id))}
          >
            Withdraw application
          </Button>
        )}
      </Group>
      <Group>
        <Badge>{APPLICATION_STATUS_LABELS[a.status]}</Badge>
        <Badge variant="light">{APPLICATION_TYPE_LABELS[a.type]}</Badge>
      </Group>
      <ApplicationApplicantSummary
        application={a}
        canViewProfile={Boolean(user?.permissions?.["member.view_list"])}
        canViewConduct={Boolean(user?.permissions?.["da.view"])}
        memberView={!a.actions.viewInternalReview || user?.id === a.applicantId}
      />
      {a.status === "approved" && (
        <Alert color="teal" title="Approved">
          Staff will handle the department changes manually.
        </Alert>
      )}
      <ApplicationReviewSection
        application={a}
        pending={action.isPending}
        onAction={(fn) => action.mutate(fn)}
      />
      {action.error && <Alert color="red">{action.error.message}</Alert>}
      {action.isPending && (
        <Text size="sm" c="dimmed" role="status">
          Saving changes…
        </Text>
      )}
      <Stack gap="md" style={{ minWidth: 0 }}>
        <Title order={2}>Questions and answers</Title>
        <ApplicationAnswersView fields={a.fields} answers={a.answers} />
      </Stack>
      <ApplicationDiscussion
        application={a}
        canViewProfiles={Boolean(user?.permissions?.["member.view_list"])}
        message={message}
        onMessageChange={setMessage}
        pending={action.isPending}
        onSend={() =>
          action.mutate(async () => {
            await applicationsApi.message(id, message.trim());
            setMessage("");
          })
        }
      />
      <ApplicationActivityLog
        application={a}
        canViewProfiles={Boolean(user?.permissions?.["member.view_list"])}
      />
      {a.actions.privateNote && (
        <Paper withBorder p="lg" radius="md" style={{ minWidth: 0 }}>
          <Stack gap="md">
            <Title order={3}>Private staff notes</Title>
            <Text size="sm" c="dimmed">
              Only staff can read these notes. They are never shown to the member.
            </Text>
            {a.staffNotes?.map((n) => (
              <Stack key={n.id} gap={4}>
                <Text size="xs" c="dimmed">
                  {n.createdAt && new Date(n.createdAt).toLocaleString()}
                </Text>
                <Text style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{n.body}</Text>
              </Stack>
            ))}
            <Textarea
              label="Add private note"
              value={privateNote}
              onChange={(e) => setPrivateNote(e.currentTarget.value)}
            />
            <Button
              disabled={action.isPending || !privateNote.trim()}
              onClick={() =>
                action.mutate(async () => {
                  await applicationsApi.privateNote(id, privateNote);
                  setPrivateNote("");
                })
              }
            >
              Add note
            </Button>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
